import {
  AnimatePresence,
  motion,
  type Transition,
  useReducedMotion,
  type Variants,
} from 'framer-motion';
import {
  AlertCircle,
  Building2,
  Check,
  Clock,
  Loader2,
  MapPin,
  MessageCircleQuestion,
  Mic,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useFeatureFlagEnabled } from '@/lib/posthog/hooks/useFeatureFlagEnabled';
import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { cn } from '@/lib/shadcn/utils';
import type { VoiceConversationLabels } from '@/modules/elevenlabs/components/voiceConversationState';
import { VoiceConversationWidget } from '@/modules/elevenlabs/components/VoiceConversationWidget';
import {
  getPrescreenBriefingStrengths,
  getRecruiterReviewTitle,
  getStrongMatchHeading,
  PRESCREEN_BRIEFING_COPY,
} from '@/modules/prescreen/briefingCopy';
import { PrescreenDocumentMetadata } from '@/modules/prescreen/components/PrescreenDocumentMetadata';
import { PrescreenEmailCollection } from '@/modules/prescreen/components/PrescreenEmailCollection';
import { PrescreenMicrophonePermission } from '@/modules/prescreen/components/PrescreenMicrophonePermission';
import { PrescreenSatisfactionScore } from '@/modules/prescreen/components/PrescreenSatisfactionScore';
import { PRESCREEN_COPY_VERSION } from '@/modules/prescreen/constants';
import { isTransientInviteError } from '@/modules/prescreen/inviteError';
import { useCreateScreeningSessionMutation } from '@/modules/prescreen/mutations/useCreateScreeningSessionMutation';
import { useTrackPrescreenCallStartedMutation } from '@/modules/prescreen/mutations/useTrackPrescreenCallStartedMutation';
import {
  getPrescreenCompletionMessage,
  getSelfServiceHiringTeamName,
  isSelfServiceOperatingMode,
  shouldShowPrescreenMeetingLink,
} from '@/modules/prescreen/prescreenIdentity';
import { usePrescreenInviteQuery } from '@/modules/prescreen/queries/usePrescreenInviteQuery';
import { usePrescreenJobQuery } from '@/modules/prescreen/queries/usePrescreenJobQuery';
import { usePrescreenNameQuery } from '@/modules/prescreen/queries/usePrescreenNameQuery';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/modules/ui/components/avatar/Avatar';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import { Heading } from '@/modules/ui/components/heading/Heading';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import type { components } from '@/types/apiSchema';
import { isLocalEnv } from '@/utils/env';

type PrescreeningJob = components['schemas']['PrescreeningJobResponse'];
type JobLocationType = components['schemas']['JobLocationType'];

type PrescreenState =
  | 'briefing'
  | 'microphone'
  | 'calling'
  | 'feedback'
  | 'endedEarly'
  | 'completed';

const recruiterImages: Record<string, string> = {
  matt: '/recruiters/matt.png',
  jana: '/recruiters/jana.png',
  ondrej: '/recruiters/ondřej.jpeg',
  juraj: '/recruiters/juraj.jpeg',
  kristyna: '/recruiters/kristyna.png',
};

function normalizeRecruiterName(name: string): string {
  return name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function getRecruiterAvatarUrl(firstName: string): string | null {
  const key = normalizeRecruiterName(firstName);
  return recruiterImages[key] ?? null;
}

const DEFAULT_RECRUITER: RecruiterInfo = {
  firstName: 'Juraj',
  avatarUrl: getRecruiterAvatarUrl('Juraj'),
};

const pageVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

const pageTransition: Transition = { duration: 0.3, ease: 'easeOut' };

const locationTypeLabels: Record<string, string> = {
  Onsite: 'On-site',
  Hybrid: 'Hybrid',
  Remote: 'Remote',
};

function normalizeBriefingLocationTypes(
  locationTypes: readonly JobLocationType[],
): JobLocationType[] {
  const hasOnsite = locationTypes.includes('Onsite');
  const hasRemote = locationTypes.includes('Remote');

  if (!hasOnsite || !hasRemote) return [...locationTypes];

  return ['Hybrid'];
}

const contractTypeLabels: Record<string, string> = {
  EmploymentContract: 'Employment contract',
  B2B: 'Contractor (B2B)',
};

const PRESCREEN_VOICE_LABELS = {
  agentSpeaking: 'Finny is speaking',
  listening: 'Listening',
  end: 'End voice intro',
  restart: 'Restart voice intro',
  finish: 'Continue',
  start: 'Start voice intro',
  retry: 'Try again',
  microphonePermissionTitle: 'Microphone access is blocked',
  microphonePermissionMessage:
    'Allow microphone access in your browser settings, then restart the voice intro.',
  connectionErrorTitle: 'We couldn’t connect your voice intro.',
  connectionErrorMessage: 'Please try again.',
} satisfies Partial<VoiceConversationLabels>;

interface RecruiterInfo {
  firstName: string;
  avatarUrl?: string | null;
}

interface PrescreenMetadataBadgeProps {
  label: string;
  description: string;
  icon?: ReactNode;
}

function PrescreenMetadataBadge({
  label,
  description,
  icon,
}: PrescreenMetadataBadgeProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${label}. ${description}`}
          className="focus-visible:ring-primary/40 cursor-help rounded-full focus-visible:ring-2 focus-visible:outline-none"
        >
          <Badge
            variant="outline"
            size="lg"
            className="text-foreground border-neutral-400 bg-neutral-100"
          >
            {icon}
            {label}
          </Badge>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        sideOffset={4}
        className="max-w-56 text-left"
      >
        {description}
      </TooltipContent>
    </Tooltip>
  );
}

function getLocationTypeRequirement(locationType: JobLocationType): string {
  switch (locationType) {
    case 'Onsite':
      return 'You’ll work on-site at the company.';
    case 'Hybrid':
      return 'You’ll split time between on-site and remote work.';
    case 'Remote':
      return 'You can work remotely for this role.';
  }
}

function getContractTypeRequirement(contractType: string): string {
  switch (contractType) {
    case 'EmploymentContract':
      return 'You’ll be hired as an employee.';
    case 'B2B':
      return 'You’ll work as an independent contractor.';
    default:
      return `This role uses a ${contractType} contract.`;
  }
}

interface PrescreenBriefingProps {
  job: PrescreeningJob;
  firstName: string;
  recruiter?: RecruiterInfo | null;
  onStart: () => void;
}

function PrescreenBriefing({
  job,
  firstName,
  recruiter,
  onStart,
}: PrescreenBriefingProps) {
  const inlineCtaRef = useRef<HTMLDivElement>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const inlineCta = inlineCtaRef.current;
    if (!inlineCta || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry?.isIntersecting),
      { threshold: 0.15 },
    );
    observer.observe(inlineCta);

    return () => observer.disconnect();
  }, []);

  const hasRoleMetadata =
    Boolean(job.location) ||
    job.locationTypes.length > 0 ||
    job.contractTypes.length > 0;
  const briefingLocationTypes = normalizeBriefingLocationTypes(
    job.locationTypes,
  );
  const briefingRecruiter = recruiter ?? DEFAULT_RECRUITER;
  const recruiterInitial = briefingRecruiter.firstName.trim().charAt(0) || 'F';
  const isSelfService = isSelfServiceOperatingMode(job.operatingMode);
  const hiringTeamName = getSelfServiceHiringTeamName(job.companyName);
  const displayedStrengths = getPrescreenBriefingStrengths(
    job.prescreenMetadata?.strength,
  );
  const briefingHeading = getStrongMatchHeading(firstName);

  return (
    <motion.div
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={pageTransition}
      className="mx-auto w-full max-w-3xl"
    >
      <header>
        <Heading
          variant="h1"
          className="text-foreground max-w-3xl text-balance md:text-4xl md:leading-10"
        >
          {briefingHeading.leading}
          {briefingHeading.firstName && (
            <span className="text-primary-700">
              {briefingHeading.firstName}
            </span>
          )}
          {briefingHeading.trailing}
        </Heading>
        <Paragraph className="text-neutral-650 mt-3 text-lg leading-7 font-medium sm:text-xl">
          {job.title} at {job.companyName}
        </Paragraph>
        {hasRoleMetadata && (
          <div
            className="mt-4 flex flex-wrap items-center gap-2"
            aria-label="Role location and work arrangement"
          >
            {job.location && (
              <PrescreenMetadataBadge
                icon={<MapPin aria-hidden="true" />}
                label={job.location}
                description={`You need to be based in ${job.location}.`}
              />
            )}
            {briefingLocationTypes.map((lt) => (
              <PrescreenMetadataBadge
                key={lt}
                label={locationTypeLabels[lt] ?? lt}
                description={getLocationTypeRequirement(lt)}
              />
            ))}
            {job.contractTypes.map((contractType) => (
              <PrescreenMetadataBadge
                key={contractType}
                label={contractTypeLabels[contractType] ?? contractType}
                description={getContractTypeRequirement(contractType)}
              />
            ))}
          </div>
        )}
      </header>

      <section
        className="bg-secondary mt-6 max-w-2xl rounded-xl p-4 sm:p-5"
        aria-labelledby="match-evidence-heading"
      >
        <Heading
          id="match-evidence-heading"
          variant="h3"
          className="text-foreground text-base"
        >
          What stood out in your profile
        </Heading>
        <ul className="mt-3 grid gap-2">
          {displayedStrengths.map((strength) => (
            <li
              key={strength.summary}
              className="flex items-start gap-2 text-sm leading-5"
            >
              <Check
                className="text-primary mt-0.5 size-4 shrink-0"
                aria-hidden="true"
              />
              <div>
                <p className="text-foreground font-medium">
                  {strength.summary}
                </p>
                <p className="text-neutral-650 mt-0.5">
                  {strength.explanation}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section
        className={cn('max-w-[72ch] border-t', 'mt-6 pt-5')}
        aria-labelledby="before-you-start-heading"
      >
        <Heading
          id="before-you-start-heading"
          variant="h3"
          className="text-foreground text-lg leading-6"
        >
          {PRESCREEN_BRIEFING_COPY.beforeYouStartHeading}
        </Heading>
        <ul className="text-neutral-650 mt-3 grid gap-3 text-sm leading-5 sm:grid-cols-3 sm:gap-4">
          <li className="flex items-start gap-2">
            <Clock
              className="text-primary mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            <div>
              <p className="text-foreground leading-5 font-medium">
                {PRESCREEN_BRIEFING_COPY.durationTitle}
              </p>
              <p>{PRESCREEN_BRIEFING_COPY.durationDetail}</p>
            </div>
          </li>
          <li className="flex items-start gap-2">
            <Building2
              className="text-primary mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            <div>
              <p className="text-foreground leading-5 font-medium">
                {PRESCREEN_BRIEFING_COPY.contextTitle}
              </p>
              <p>{PRESCREEN_BRIEFING_COPY.contextDetail}</p>
            </div>
          </li>
          <li className="flex items-start gap-2">
            <MessageCircleQuestion
              className="text-primary mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            <div>
              <p className="text-foreground leading-5 font-medium">
                {PRESCREEN_BRIEFING_COPY.questionsTitle}
              </p>
              <p>{PRESCREEN_BRIEFING_COPY.questionsDetail}</p>
            </div>
          </li>
        </ul>
      </section>

      <section
        className="bg-secondary/50 mt-5 max-w-[72ch] rounded-xl border p-4 sm:p-5"
        aria-labelledby="who-reviews-heading"
      >
        <Heading
          id="who-reviews-heading"
          variant="h3"
          className="text-foreground text-base"
        >
          {PRESCREEN_BRIEFING_COPY.whoReviewsHeading}
        </Heading>
        <div className="mt-3 flex items-center gap-3">
          {isSelfService ? (
            <div className="bg-secondary-150 text-primary-700 flex size-10 shrink-0 items-center justify-center rounded-full">
              <Building2 className="size-5" aria-hidden="true" />
            </div>
          ) : (
            <Avatar className="size-10 shrink-0">
              {briefingRecruiter.avatarUrl && (
                <AvatarImage
                  src={briefingRecruiter.avatarUrl}
                  alt=""
                  className="object-cover"
                />
              )}
              <AvatarFallback className="bg-secondary-150 text-primary-700 font-semibold">
                {recruiterInitial.toUpperCase()}
              </AvatarFallback>
            </Avatar>
          )}
          <div>
            <p className="text-foreground text-sm font-medium sm:text-base">
              {isSelfService
                ? hiringTeamName
                : getRecruiterReviewTitle(briefingRecruiter.firstName)}
            </p>
            <p className="text-neutral-650 text-sm leading-5">
              {PRESCREEN_BRIEFING_COPY.recruiterReviewDetail}
            </p>
          </div>
        </div>
        <p className="text-neutral-650 mt-3 border-t pt-3 text-xs leading-5 sm:text-sm">
          {PRESCREEN_BRIEFING_COPY.hiringDecisionDetail}
        </p>
      </section>

      <div ref={inlineCtaRef} className="mt-5 max-w-[72ch]">
        <Button
          variant="primary"
          size="lg"
          className="h-14 w-full gap-2 rounded-2xl text-base font-semibold sm:text-lg"
          onClick={onStart}
        >
          <Mic className="h-5 w-5" aria-hidden="true" />
          {PRESCREEN_BRIEFING_COPY.cta}
        </Button>
      </div>

      <div className="text-neutral-650 mt-6 flex items-start justify-center gap-2 border-t pt-6 text-center text-xs leading-5">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <p>
          {PRESCREEN_BRIEFING_COPY.dataSharing}{' '}
          <a
            href="https://www.finlay.ai/privacy-notice"
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground hover:text-primary underline underline-offset-2 transition-colors"
          >
            {PRESCREEN_BRIEFING_COPY.privacyLink}
          </a>
        </p>
      </div>

      <AnimatePresence>
        {showStickyCta && (
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? undefined : { opacity: 0, y: 12 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
            className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center p-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] sm:hidden"
          >
            <div className="bg-background/95 pointer-events-auto w-full max-w-2xl rounded-2xl border p-2 shadow-lg backdrop-blur-sm">
              <Button
                variant="primary"
                size="lg"
                className="h-12 w-full gap-2 rounded-xl font-semibold motion-reduce:transition-none"
                onClick={onStart}
                aria-label={PRESCREEN_BRIEFING_COPY.cta}
              >
                <Mic className="h-4 w-4" aria-hidden="true" />
                {PRESCREEN_BRIEFING_COPY.cta}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

interface PrescreenCallingProps {
  candidateJobId: string;
  firstName: string;
  jobTitle: string;
  companyName: string;
  openingContext: string;
  operatingMode: components['schemas']['OperatingMode'];
  microphoneAccessGranted: boolean;
  onConnect: (conversationId: string) => void;
  onFinish: () => void;
  onConfirmedEnd: () => void;
  onError: (error: Error) => void;
}

function PrescreenCalling({
  candidateJobId,
  firstName,
  jobTitle,
  companyName,
  openingContext,
  operatingMode,
  microphoneAccessGranted,
  onConnect,
  onFinish,
  onConfirmedEnd,
  onError,
}: PrescreenCallingProps) {
  const screeningSession = useCreateScreeningSessionMutation();
  const [pendingEndSession, setPendingEndSession] = useState<
    (() => Promise<void>) | null
  >(null);
  const [isVoiceIntroComplete, setIsVoiceIntroComplete] = useState(false);
  const candidateFirstName = firstName.trim();
  const handleConnect = (conversationId: string) => {
    setIsVoiceIntroComplete(false);
    onConnect(conversationId);
  };

  const handleConfirmedEnd = async () => {
    if (!pendingEndSession) return;

    try {
      await pendingEndSession();
      onConfirmedEnd();
    } catch (error) {
      onError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setPendingEndSession(null);
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={pageTransition}
      className="mx-auto flex w-full max-w-2xl flex-col items-center"
    >
      <div className="mb-8 text-center">
        {isVoiceIntroComplete ? (
          <>
            <Heading variant="h2" className="text-foreground mb-2">
              Your voice intro is complete
            </Heading>
            <Paragraph className="text-muted-foreground">
              Thanks for sharing your answers.
            </Paragraph>
          </>
        ) : (
          <>
            <Heading variant="h2" className="text-foreground mb-2">
              Go ahead
              {candidateFirstName && <>, {candidateFirstName}</>}
            </Heading>
            <Paragraph className="text-muted-foreground">
              Take your time. Finny will guide you through a few questions.
            </Paragraph>
          </>
        )}
      </div>

      <div className="w-full max-w-xl">
        <VoiceConversationWidget
          authorization={{
            getSignedUrl: () =>
              screeningSession.mutateAsync({ candidateJobId }),
          }}
          dynamicVariables={{
            candidate_name: firstName,
            job_name: jobTitle,
            company_name: companyName,
            opening_context: openingContext,
            operating_mode: operatingMode,
            candidate_job_id: candidateJobId,
            analytics_tracking_version: 'server_v1',
            copy_version: PRESCREEN_COPY_VERSION,
          }}
          autoStart
          microphoneAccessGranted={microphoneAccessGranted}
          onConnect={handleConnect}
          onDisconnect={(details) =>
            setIsVoiceIntroComplete(details.reason === 'agent')
          }
          onFinish={onFinish}
          onError={onError}
          onEndRequested={(endSession) =>
            setPendingEndSession(() => endSession)
          }
          labels={{
            ...PRESCREEN_VOICE_LABELS,
            ...(screeningSession.isError && {
              connectionErrorMessage:
                'We can’t start this interview right now. Please try again or contact your hiring team.',
            }),
          }}
          showInlineErrors
          showErrorToast={false}
        />
        <ConfirmDialog
          open={pendingEndSession !== null}
          onOpenChange={(open) => !open && setPendingEndSession(null)}
          title="End this voice intro?"
          description="If you end now, your answers will not be shared. You can start your voice intro again later."
          cancelText="Keep talking"
          confirmText="End without sharing"
          variant="destructive"
          onCancel={() => setPendingEndSession(null)}
          onConfirm={() => void handleConfirmedEnd()}
        />
      </div>
    </motion.div>
  );
}

const completedStaggerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.3,
    },
  },
};

const completedItemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

function AnimatedSuccessIcon() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <svg viewBox="0 0 96 96" className="h-20 w-20" fill="none">
      <motion.circle
        cx="48"
        cy="48"
        r="46"
        fill="#f0edff"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.4,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={{ originX: '48px', originY: '48px' }}
      />
      <motion.circle
        cx="48"
        cy="48"
        r="44"
        stroke="#6b4eff"
        strokeWidth="2.5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.5,
          delay: shouldReduceMotion ? 0 : 0.15,
          ease: 'easeOut',
        }}
      />
      <motion.path
        d="M 27 50 L 42 65 L 70 34"
        stroke="#6b4eff"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.35,
          delay: shouldReduceMotion ? 0 : 0.45,
          ease: 'easeOut',
        }}
      />
    </svg>
  );
}

interface PrescreenCompleteProps {
  firstName: string;
  recruiterFirstName: string;
  companyName: string;
  operatingMode: components['schemas']['OperatingMode'];
  meetingUrl?: string | null;
}

function PrescreenComplete({
  firstName,
  recruiterFirstName,
  companyName,
  operatingMode,
  meetingUrl,
}: PrescreenCompleteProps) {
  const isEmailCollectionEnabled =
    useFeatureFlagEnabled('prescreenEmailCollection') === true;
  const isSelfService = isSelfServiceOperatingMode(operatingMode);
  const contactName = isSelfService
    ? getSelfServiceHiringTeamName(companyName)
    : recruiterFirstName;
  const completionMessage = getPrescreenCompletionMessage({
    operatingMode,
    companyName,
    recruiterFirstName,
  });

  return (
    <motion.div
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={pageTransition}
      className="mx-auto w-full max-w-lg text-center"
    >
      <div className="mb-6 flex justify-center">
        <AnimatedSuccessIcon />
      </div>

      <motion.div
        variants={completedStaggerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={completedItemVariants}>
          <Heading variant="h2" className="text-foreground mb-3">
            You’re all set, {firstName}.
            <span className="text-muted-foreground mt-2 block text-base font-normal">
              {completionMessage}
            </span>
          </Heading>
        </motion.div>

        {isEmailCollectionEnabled && (
          <motion.div
            variants={completedItemVariants}
            className="mt-8 mb-6 border-y py-6"
          >
            <PrescreenEmailCollection contactName={contactName} />
          </motion.div>
        )}

        {meetingUrl &&
          shouldShowPrescreenMeetingLink(operatingMode, meetingUrl) && (
            <motion.div
              variants={completedItemVariants}
              className={isEmailCollectionEnabled ? undefined : 'mt-8'}
            >
              <p className="text-muted-foreground text-sm">
                Prefer a live call?{' '}
                <a
                  href={meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground hover:text-primary underline underline-offset-2 transition-colors"
                >
                  Book a call with {contactName}
                </a>
              </p>
            </motion.div>
          )}
      </motion.div>
    </motion.div>
  );
}

interface PrescreenEndedEarlyProps {
  onRestart: () => void;
}

function PrescreenEndedEarly({ onRestart }: PrescreenEndedEarlyProps) {
  return (
    <motion.div
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={pageTransition}
      className="mx-auto w-full max-w-lg text-center"
    >
      <div className="mb-6 flex justify-center">
        <div className="border-primary/20 bg-secondary text-primary flex h-20 w-20 items-center justify-center rounded-full border">
          <RotateCcw className="h-9 w-9" aria-hidden="true" />
        </div>
      </div>

      <motion.div
        variants={completedStaggerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={completedItemVariants}>
          <Heading variant="h2" className="text-foreground mb-3">
            You ended your voice intro early
          </Heading>
          <Paragraph className="text-muted-foreground">
            Your answers weren’t shared. You can start again when you’re ready.
          </Paragraph>
        </motion.div>

        <motion.div variants={completedItemVariants} className="mt-8">
          <Button
            variant="primary"
            size="lg"
            className="gap-2"
            onClick={onRestart}
          >
            <RotateCcw className="h-5 w-5" aria-hidden="true" />
            Start voice intro again
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

interface PrescreenFeedbackProps {
  candidateJobId: string;
  jobId: string;
  onComplete: () => void;
}

function PrescreenFeedback({
  candidateJobId,
  jobId,
  onComplete,
}: PrescreenFeedbackProps) {
  return (
    <motion.div
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={pageTransition}
      className="mx-auto w-full max-w-lg text-center"
    >
      <Heading variant="h2" className="text-foreground mb-3">
        Your responses are recorded
      </Heading>
      <Paragraph className="text-muted-foreground">
        Thanks for sharing your voice intro. Your responses have been received.
      </Paragraph>
      <div className="mt-8">
        <PrescreenSatisfactionScore
          candidateJobId={candidateJobId}
          jobId={jobId}
          onComplete={onComplete}
        />
      </div>
    </motion.div>
  );
}

interface PrescreenUnavailableProps {
  title?: string;
  description?: string;
}

function PrescreenUnavailable({
  title = 'This link is no longer active',
  description = "This voice intro link has expired or isn't valid. Contact the recruiter who sent it to get a new one.",
}: PrescreenUnavailableProps) {
  return (
    <div className="flex min-h-screen flex-col bg-linear-to-br from-neutral-50 to-neutral-100">
      <PrescreenDocumentMetadata />
      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col items-center justify-center px-4 py-8 sm:px-6 md:py-12 lg:px-8">
        <div className="flex flex-col items-center text-center">
          <div className="bg-destructive/10 mb-6 flex h-16 w-16 items-center justify-center rounded-full">
            <AlertCircle className="text-destructive h-8 w-8" />
          </div>

          <Heading variant="h3" className="text-foreground mb-3">
            {title}
          </Heading>

          <Paragraph>{description}</Paragraph>
        </div>
      </main>
    </div>
  );
}

function PrescreenLoading() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-linear-to-br from-neutral-50 to-neutral-100">
      <PrescreenDocumentMetadata />
      <Loader2 className="text-primary mb-4 h-8 w-8 animate-spin" />
      <Paragraph>Getting your voice intro ready...</Paragraph>
    </div>
  );
}

interface CandidatePrescreenProps {
  candidateJobId?: string;
}

export const CandidatePrescreen = ({
  candidateJobId: resolvedCandidateJobId,
}: CandidatePrescreenProps = {}) => {
  const { candidateJobId: routeCandidateJobId } = useParams<{
    candidateJobId: string;
  }>();
  const candidateJobId = resolvedCandidateJobId ?? routeCandidateJobId;
  const [state, setState] = useState<PrescreenState>('briefing');
  const [microphoneAccessGranted, setMicrophoneAccessGranted] = useState(false);

  const hasTrackedPrescreenStartClick = useRef(false);
  const hasTrackedPrescreenCallStart = useRef(false);
  const trackEvent = useTrackEvent();
  const posthog = usePostHog();
  const trackPrescreenCallStartedMutation =
    useTrackPrescreenCallStartedMutation();

  const {
    data: candidateName,
    isLoading: isNameLoading,
    isError: isNameError,
  } = usePrescreenNameQuery(candidateJobId ?? '');

  const {
    data: job,
    isLoading: isJobLoading,
    isError: isJobError,
  } = usePrescreenJobQuery(candidateJobId ?? '');

  useEffect(() => {
    if (!candidateJobId || isLocalEnv || !candidateName || !job) {
      return;
    }

    posthog.identify(candidateJobId, {
      firstName: candidateName.firstName,
      lastName: candidateName.lastName,
    });
  }, [candidateJobId, candidateName, job, posthog]);

  useEffect(() => {
    return () => {
      posthog.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrescreenStart = () => {
    if (!hasTrackedPrescreenStartClick.current && job && candidateName) {
      hasTrackedPrescreenStartClick.current = true;
      trackEvent('prescreenStartClicked', {
        jobId: job.id,
        firstName: candidateName.firstName,
        lastName: candidateName.lastName,
        companyName: job.companyName,
        briefingVersion: PRESCREEN_COPY_VERSION,
        copyVersion: PRESCREEN_COPY_VERSION,
      });
    }

    setMicrophoneAccessGranted(false);
    setState('microphone');
  };

  const handlePrescreenCallConnect = (conversationId: string) => {
    if (hasTrackedPrescreenCallStart.current || !candidateJobId) {
      return;
    }

    hasTrackedPrescreenCallStart.current = true;
    trackPrescreenCallStartedMutation.mutate({
      candidateJobId,
      conversationId,
      copyVersion: PRESCREEN_COPY_VERSION,
    });
  };

  const handlePrescreenCallFailed = (error: Error) => {
    if (!job) {
      return;
    }

    trackEvent('prescreenCallFailed', {
      jobId: job.id,
      firstName: candidateName?.firstName ?? '',
      lastName: candidateName?.lastName ?? '',
      companyName: job.companyName,
      reason: error.message,
      copyVersion: PRESCREEN_COPY_VERSION,
    });
  };

  const isLoading = isNameLoading || isJobLoading;
  const isError = isNameError || isJobError;

  if (!candidateJobId || isError) {
    return <PrescreenUnavailable />;
  }

  if (isLoading) {
    return <PrescreenLoading />;
  }

  if (
    !job ||
    (isSelfServiceOperatingMode(job.operatingMode) && !job.companyName.trim())
  ) {
    if (job && isSelfServiceOperatingMode(job.operatingMode)) {
      return (
        <PrescreenUnavailable
          title="This voice intro is unavailable"
          description="This voice intro can’t start because the hiring company isn’t set up. Contact the team that sent this link."
        />
      );
    }

    return <PrescreenUnavailable />;
  }

  const firstName = candidateName?.firstName ?? '';

  const recruiter: RecruiterInfo = job?.recruiterFirstname
    ? {
        firstName: job.recruiterFirstname,
        avatarUrl: getRecruiterAvatarUrl(job.recruiterFirstname),
      }
    : DEFAULT_RECRUITER;

  return (
    <div
      className={cn(
        'flex min-h-screen flex-col transition-colors duration-500',
        state === 'briefing'
          ? 'bg-background'
          : 'bg-linear-to-br from-neutral-50 to-neutral-100',
        state === 'calling' && 'from-secondary/20 to-neutral-100',
      )}
    >
      <PrescreenDocumentMetadata
        companyName={job?.companyName}
        jobTitle={job?.title}
      />
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center px-4 py-10 sm:px-6 md:py-16 lg:px-8">
        <AnimatePresence mode="wait">
          {state === 'briefing' && job && (
            <PrescreenBriefing
              key="briefing"
              job={job}
              firstName={firstName}
              recruiter={recruiter}
              onStart={handlePrescreenStart}
            />
          )}

          {state === 'microphone' && (
            <PrescreenMicrophonePermission
              key="microphone"
              onStart={(accessGranted) => {
                setMicrophoneAccessGranted(accessGranted);
                setState('calling');
              }}
              onError={handlePrescreenCallFailed}
            />
          )}

          {state === 'calling' && job && (
            <PrescreenCalling
              key="calling"
              candidateJobId={candidateJobId}
              firstName={firstName}
              jobTitle={job.title}
              companyName={job.companyName}
              openingContext={job.openingContext}
              operatingMode={job.operatingMode}
              microphoneAccessGranted={microphoneAccessGranted}
              onConnect={handlePrescreenCallConnect}
              onFinish={() => setState('feedback')}
              onConfirmedEnd={() => setState('endedEarly')}
              onError={handlePrescreenCallFailed}
            />
          )}

          {state === 'feedback' && (
            <PrescreenFeedback
              key="feedback"
              candidateJobId={candidateJobId}
              jobId={job?.id ?? ''}
              onComplete={() => setState('completed')}
            />
          )}

          {state === 'completed' && (
            <PrescreenComplete
              key="completed"
              firstName={firstName}
              recruiterFirstName={recruiter.firstName}
              companyName={job.companyName}
              operatingMode={job.operatingMode}
              meetingUrl={job?.meetingUrl}
            />
          )}

          {state === 'endedEarly' && (
            <PrescreenEndedEarly
              key="endedEarly"
              onRestart={() => setState('calling')}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export const CandidatePrescreenInvite = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, isError, error } = usePrescreenInviteQuery(
    slug ?? '',
  );
  const candidateJobId = data?.candidateJobId;
  const trackEvent = useTrackEvent();

  const isUnavailable = !slug || isError || (!isLoading && !candidateJobId);

  const hasTrackedUnavailable = useRef(false);
  useEffect(() => {
    if (!isUnavailable || hasTrackedUnavailable.current) {
      return;
    }
    hasTrackedUnavailable.current = true;

    const reason = !slug
      ? 'missing_slug'
      : isError && isTransientInviteError(error)
        ? 'server_error'
        : 'link_invalid';

    trackEvent('prescreenInviteUnavailable', { slug: slug ?? '', reason });
  }, [isUnavailable, slug, isError, error, trackEvent]);

  if (isUnavailable) {
    return <PrescreenUnavailable />;
  }

  if (isLoading) {
    return <PrescreenLoading />;
  }

  return <CandidatePrescreen candidateJobId={candidateJobId} />;
};
